import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import React from "react";
import { ContainerCard } from "../../../../components";
import { Tooltip } from "../../../../components/Tooltip";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import { ActivityLineChartDataKey } from "../hooks/useInternalChartData";
import {
  MetricType,
  formatAvgSessionDuration,
  getAvgSessionDurationMinutes,
  getDisplayValue,
} from "../utils/usageUtils";

const VALUE_SKELETON_WIDTH = 96;
const VALUE_SKELETON_HEIGHT = 40;

const AVG_SESSION_DURATION_KEY = "avgSessionDuration";

const METRICS: Array<{
  key: ActivityLineChartDataKey;
  metricType: MetricType;
  labelKey: string;
  tooltipKey: string;
}> = [
  {
    key: ActivityLineChartDataKey.UserSessions,
    metricType: MetricType.USER_SESSIONS,
    labelKey: "pages.dataAnalytics.internalUsage.activity.metrics.userSessions",
    tooltipKey:
      "pages.dataAnalytics.internalUsage.activity.tooltips.userSessions",
  },
  {
    key: ActivityLineChartDataKey.HoursInVr,
    metricType: MetricType.HOURS_IN_VR,
    labelKey: "pages.dataAnalytics.internalUsage.activity.metrics.hoursInVr",
    tooltipKey: "pages.dataAnalytics.internalUsage.activity.tooltips.hoursInVr",
  },
  {
    key: ActivityLineChartDataKey.UniqueActiveUsers,
    metricType: MetricType.UNIQUE_ACTIVE_USERS,
    labelKey:
      "pages.dataAnalytics.internalUsage.activity.metrics.uniqueActiveUsers",
    tooltipKey:
      "pages.dataAnalytics.internalUsage.activity.tooltips.uniqueActiveUsers",
  },
];

export interface ActivitySummaryCardsProps {
  /** Aggregated value per metric for the selected period */
  aggregateValues: Partial<Record<ActivityLineChartDataKey, number | string>>;
  /** Renders placeholders instead of the values while the metrics are loading */
  isLoading?: boolean;
  /** Shows a progress bar on each card while new data is being fetched */
  isFetching?: boolean;
  isUserLevelDataEnabled?: boolean;
}

interface ActivitySummaryCardProps {
  metricKey: string;
  label: string;
  value: string;
  tooltip?: string;
  isLoading: boolean;
  isFetching: boolean;
}

const ActivitySummaryCard: React.FC<ActivitySummaryCardProps> = ({
  metricKey,
  label,
  value,
  tooltip,
  isLoading,
  isFetching,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  return (
    <ContainerCard
      data-testid={`activity-summary-card-${metricKey}`}
      isFetching={isFetching && !isLoading}
      sx={{ gap: 0, minWidth: 0 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "row", md: "column" },
          alignItems: { xs: "center", md: "flex-start" },
          justifyContent: { xs: "space-between", md: "flex-start" },
          gap: { xs: 1, md: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: { xs: "flex-start", md: "space-between" },
            gap: 1,
            width: { md: "100%" },
          }}
        >
          <Typography
            sx={{ color: theme.colors.text[6], fontSize: 14, fontWeight: 700 }}
          >
            {label}
          </Typography>
          {tooltip && (
            <Tooltip message={tooltip} placement={isMobile ? "top" : "left"}>
              <InfoOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: theme.colors.secondary[6],
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
        </Box>

        {isLoading ? (
          <Skeleton
            height={VALUE_SKELETON_HEIGHT}
            variant="rounded"
            width={VALUE_SKELETON_WIDTH}
          />
        ) : (
          <Typography
            sx={{
              color: theme.colors.text[6],
              fontSize: { xs: 24, md: 36 },
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </ContainerCard>
  );
};

export const ActivitySummaryCards: React.FC<ActivitySummaryCardsProps> = ({
  aggregateValues,
  isLoading = false,
  isFetching = false,
  isUserLevelDataEnabled = false,
}) => {
  const { t } = useLocalization();

  const cards: Array<
    Pick<ActivitySummaryCardProps, "label" | "metricKey" | "tooltip" | "value">
  > = METRICS.map(({ key, metricType, labelKey, tooltipKey }) => {
    const rawValue = Number(aggregateValues[key] ?? 0);

    return {
      metricKey: key,
      label: t(labelKey),
      tooltip: t(tooltipKey),
      value: Number.isFinite(rawValue)
        ? getDisplayValue(rawValue, metricType, isUserLevelDataEnabled)
        : String(aggregateValues[key]),
    };
  });

  const avgSessionDuration = getAvgSessionDurationMinutes(
    Number(aggregateValues[ActivityLineChartDataKey.HoursInVr] ?? 0),
    Number(aggregateValues[ActivityLineChartDataKey.UserSessions] ?? 0),
    isUserLevelDataEnabled,
  );

  cards.push({
    metricKey: AVG_SESSION_DURATION_KEY,
    label: t(
      "pages.dataAnalytics.internalUsage.activity.metrics.avgSessionDuration",
    ),
    tooltip: t(
      avgSessionDuration === null
        ? "pages.dataAnalytics.internalUsage.activity.tooltips.avgSessionDurationUnavailable"
        : "pages.dataAnalytics.internalUsage.activity.tooltips.avgSessionDuration",
    ),
    value:
      avgSessionDuration === null
        ? t("pages.dataAnalytics.internalUsage.activity.notAvailable")
        : formatAvgSessionDuration(avgSessionDuration),
  });

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: `repeat(${cards.length}, minmax(0, 1fr))`,
        },
        gap: 3,
        alignItems: "stretch",
      }}
    >
      {cards.map(({ metricKey, label, value, tooltip }) => (
        <ActivitySummaryCard
          isFetching={isFetching}
          isLoading={isLoading}
          key={metricKey}
          label={label}
          metricKey={metricKey}
          tooltip={tooltip}
          value={value}
        />
      ))}
    </Box>
  );
};
